// src/components/shared/VoiceAlertsControl.js
//
// The switch for spoken alerts, and the choice of voice.
//
// Off by default, and deliberately so. A web app that starts talking without
// being asked is the kind of thing people close the tab over — and in a shared
// dispatch office it would be talking over somebody else's phone call.
//
// The preview button matters more than it looks. Browsers refuse to speak
// until the page has had a real user interaction, machines are muted, and the
// available voices vary by operating system. Somebody turning this on needs to
// find all of that out in the second they switch it on, not during the first
// alert that mattered.

import React, { useEffect, useState } from 'react';
import {
  isSupported, listVoices, getSettings, saveSettings, preview, silence,
} from '../../utils/announcer';

export default function VoiceAlertsControl() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(getSettings);
  const [voices, setVoices] = useState([]);

  // Voices load asynchronously, and in some browsers the first call returns an
  // empty list — the event fires once they are ready.
  useEffect(() => {
    if (!isSupported()) return undefined;
    const load = () => setVoices(listVoices());
    load();
    window.speechSynthesis.addEventListener?.('voiceschanged', load);
    return () => window.speechSynthesis.removeEventListener?.('voiceschanged', load);
  }, []);

  if (!isSupported()) return null;

  const update = (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    saveSettings(patch);
    if (patch.enabled === false) silence();
  };

  const voiceName = voices.find(v => v.voiceURI === settings.voiceURI)?.name;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        title={settings.enabled
          ? `Spoken alerts on${voiceName ? ` — ${voiceName}` : ''}`
          : 'Spoken alerts off'}
        className={`flex items-center gap-1 px-2 py-1 rounded-md text-sm transition ${
          settings.enabled
            ? 'bg-teal-50 text-teal-700 border border-teal-300'
            : 'text-gray-400 hover:text-gray-600 border border-transparent'
        }`}
      >
        {settings.enabled ? '🔊' : '🔇'}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-50 p-3">
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={settings.enabled}
                onChange={(e) => update({ enabled: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm font-semibold text-gray-800">Speak alerts aloud</span>
            </label>

            <p className="text-[11px] text-gray-500 mb-3 leading-snug">
              Says a driver&rsquo;s name when they go empty, stay empty for over an
              hour, or share their route. Nothing is announced for what was
              already true when you opened the page.
            </p>

            <label className="block text-xs font-medium text-gray-600 mb-1">Voice</label>
            <select
              value={settings.voiceURI || ''}
              onChange={(e) => update({ voiceURI: e.target.value || null })}
              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs mb-2"
            >
              <option value="">System default</option>
              {voices.map(v => (
                <option key={v.voiceURI} value={v.voiceURI}>{v.name}</option>
              ))}
            </select>

            <label className="block text-xs font-medium text-gray-600 mb-1">
              Speed — {settings.rate?.toFixed(1)}×
            </label>
            <input
              type="range" min="0.7" max="1.4" step="0.1"
              value={settings.rate}
              onChange={(e) => update({ rate: Number(e.target.value) })}
              className="w-full mb-3"
            />

            <button
              onClick={() => preview('James is empty in Dallas, Texas.')}
              className="w-full py-1.5 rounded-md bg-gray-100 hover:bg-gray-200 text-xs font-medium text-gray-700"
            >
              Test the voice
            </button>

            <p className="text-[10px] text-gray-400 mt-2 leading-snug">
              Uses your browser&rsquo;s own voices, so nothing is sent anywhere and it
              works offline. Which voices you get depends on your operating system.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
